import React from 'react';
import {
    Search,
    Grid,
    List,
    Shield,
    Zap,
    Layout,
    Code,
    Globe,
    Star,
    Layers,
    PenTool,
    MessageSquare,
    DollarSign,
    Box,
    Home
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const NavItem = ({ label, icon: Icon, active, onClick, count }) => (
    <button
        onClick={onClick}
        className={`
      w-full flex items-center gap-3 px-6 py-2 text-sm transition-all group
      ${active
                ? 'text-[var(--fg-primary)] font-medium bg-[var(--bg-panel)] border-r-2 border-[var(--fg-primary)]'
                : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] border-r-2 border-transparent'
            }
    `}
    >
        <Icon size={16} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
        <span className="tracking-tight">{label}</span>
    </button>
);

const AppLayout = ({
    children,
    onSearch: providedOnSearch,
    currentCategory,
    onCategoryChange: providedOnCategoryChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const onCategoryChange = (val) => {
        if (providedOnCategoryChange) {
            providedOnCategoryChange(val);
        } else {
            navigate(`/index?category=${val}`);
        }
    };

    const handleSearch = (val) => {
        if (providedOnSearch) {
            providedOnSearch(val);
        } else {
            // Navigate to finder with query
            navigate(`/index?search=${encodeURIComponent(val)}`);
        }
    };


    return (
        <div className="flex h-screen bg-[var(--bg-main)] text-[var(--fg-primary)] font-sans overflow-hidden">

            {/* Sidebar: The Index */}
            <div className="w-72 flex-shrink-0 flex flex-col border-r border-[var(--border-main)] bg-[var(--bg-main)]">
                {/* Brand */}
                <div className="h-20 flex items-center px-8 border-b border-[var(--border-main)]">
                    <div className="flex flex-col">
                        <span className="font-serif text-2xl font-bold tracking-tight text-[var(--fg-primary)]">AltFinder.</span>
                        <span className="text-[10px] uppercase tracking-widest text-[var(--fg-tertiary)] font-mono">Index v2.0</span>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto py-8 space-y-8">

                    {/* Section 1: Explore */}
                    <div>
                        <div className="px-6 mb-4 flex items-center gap-3">
                            <span className="h-px w-4 bg-[var(--border-highlight)]"></span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-tertiary)]">Explore</span>
                        </div>
                        <div className="space-y-1">
                            <NavItem
                                icon={Home}
                                label="Home"
                                active={location.pathname === '/'}
                                onClick={() => navigate('/')}
                            />
                            <NavItem
                                icon={Layout}
                                label="Search Index"
                                active={location.pathname === '/index' && (currentCategory === 'all' || !currentCategory)}
                                onClick={() => onCategoryChange('all')}
                            />
                            <NavItem
                                icon={Layers}
                                label="Domain Taxonomy"
                                active={location.pathname === '/domains'}
                                onClick={() => navigate('/domains')}
                            />
                            <NavItem
                                icon={Star}
                                label="Editor's Choice"
                                active={currentCategory === 'recommended'}
                                onClick={() => onCategoryChange('recommended')}
                            />
                        </div>
                    </div>

                    {/* Section 2: Categories */}
                    <div>
                        <div className="px-6 mb-4 flex items-center gap-3">
                            <span className="h-px w-4 bg-[var(--border-highlight)]"></span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-tertiary)]">Domains</span>
                        </div>
                        <div className="space-y-1">
                            <NavItem icon={Code} label="Development" active={currentCategory === 'dev'} onClick={() => onCategoryChange('dev')} />
                            <NavItem icon={PenTool} label="Design & Creative" active={currentCategory === 'design'} onClick={() => onCategoryChange('design')} />
                            <NavItem icon={MessageSquare} label="Communication" active={currentCategory === 'comms'} onClick={() => onCategoryChange('comms')} />
                            <NavItem icon={DollarSign} label="Finance" active={currentCategory === 'finance'} onClick={() => onCategoryChange('finance')} />
                            <NavItem icon={Grid} label="Productivity" active={currentCategory === 'prod'} onClick={() => onCategoryChange('prod')} />
                            <NavItem icon={Shield} label="Privacy Tools" active={currentCategory === 'privacy'} onClick={() => onCategoryChange('privacy')} />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-8 border-t border-[var(--border-main)]">
                    <p className="font-serif text-sm italic text-[var(--fg-tertiary)] mb-4">
                        "The best alternative is the one you own."
                    </p>
                    <div className="flex gap-4 text-xs font-mono text-[var(--fg-tertiary)] uppercase tracking-wider">
                        <Link to="/about" className="hover:text-[var(--fg-primary)]">Manifesto</Link>
                        <Link to="/install" className="hover:text-[var(--fg-primary)]">Install</Link>
                        <Link to="/legal" className="hover:text-[var(--fg-primary)]">Legal</Link>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 bg-[var(--bg-main)]">

                {/* Global Search Bar - Refined */}
                <div className="h-20 border-b border-[var(--border-main)] bg-[var(--bg-main)] flex items-center px-10 gap-4">
                    <Search size={20} className="text-[var(--fg-secondary)]" />
                    <input
                        type="text"
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search the index..."
                        className="flex-1 bg-transparent border-none outline-none text-2xl font-serif text-[var(--fg-primary)] placeholder-[var(--fg-tertiary)]"
                    />
                </div>

                {/* Viewport */}
                <div className="flex-1 overflow-hidden relative">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default AppLayout;

